import { getSocketClient } from './socket';

interface LockPayload {
    componentId: string;
    actionName: string;
    errorMessage?: string;
    success?: boolean;
    available?: boolean;
}

interface LockStatusResponse {
    success: boolean;
    available: boolean;
}

type LockResolve = (value: boolean) => void;
type LockReject = (reason?: unknown) => void;

declare global {
    interface Window {
        $store?: {
            dispatch: (action: string) => void;
        };
    }
}

class LockService {
    static requestLock(componentId: string, actionName: string, timeout = 5000): Promise<boolean> {
        return new Promise((resolve: LockResolve, reject: LockReject) => {
            const socket = getSocketClient();
            if (!socket) {
                return resolve(false);
            }
            socket.emit('LOCK_REQUEST', { componentId, actionName });

            const cleanup = () => {
                socket.off('LOCK_ACQUIRED', onLockAcquired);
                socket.off('LOCK_IN_USE', onLockInUse);
                socket.off('LOCK_ERROR', onLockError);
                clearTimeout(timeoutId);
            };

            const onLockAcquired = (payload: LockPayload) => {
                if (payload.componentId === componentId && payload.actionName === actionName) {
                    cleanup();
                    resolve(true);
                }
            };

            const onLockInUse = (payload: LockPayload) => {
                if (
                    !payload.componentId ||
                    (payload.componentId === componentId && payload.actionName === actionName)
                ) {
                    cleanup();
                    resolve(false);
                }
            };

            const onLockError = (payload: LockPayload) => {
                if (
                    !payload.componentId ||
                    (payload.componentId === componentId && payload.actionName === actionName)
                ) {
                    cleanup();
                    reject(new Error(payload.errorMessage || 'Lock error'));
                }
            };

            socket.on('LOCK_ACQUIRED', onLockAcquired);
            socket.on('LOCK_IN_USE', onLockInUse);
            socket.on('LOCK_ERROR', onLockError);

            const timeoutId = setTimeout(() => {
                cleanup();
                reject(
                    new Error(
                        `Lock request timed out for componentId=${componentId}, action=${actionName}`,
                    ),
                );
            }, timeout);
        });
    }

    static releaseLock(componentId: string, actionName: string): Promise<boolean> {
        return new Promise((resolve: LockResolve) => {
            const socket = getSocketClient();
            if (socket) {
                socket.emit('LOCK_RELEASE', { componentId, actionName });
            }
            const onLockReleased = (data: LockPayload) => {
                if (data.componentId === componentId) {
                    cleanup();
                    resolve(true);
                    if (window?.$store) {
                        window.$store.dispatch('resetLockFrom');
                    }
                } else if (!data.success) {
                    cleanup();
                    resolve(false);
                } else {
                    cleanup();
                    resolve(false);
                }
            };
            const cleanup = () => {
                if (socket) {
                    socket.off('LOCK_RELEASED', onLockReleased);
                    socket.off('LOCK_BROADCAST_RELEASED', onLockReleased);
                }
                clearTimeout(timeoutId);
            };

            if (socket) {
                socket.once('LOCK_RELEASED', onLockReleased);
                socket.once('LOCK_BROADCAST_RELEASED', onLockReleased);
            }

            const timeoutId = setTimeout(() => {
                cleanup();
                if (window?.$store) {
                    window.$store.dispatch('resetLockFrom');
                }
                resolve(true);
            }, 2500);
        });
    }

    static getLockStatus(componentId: string, actionName: string): Promise<boolean> {
        return new Promise((resolve: LockResolve) => {
            const socket = getSocketClient();
            if (socket) {
                socket.emit('GET_LOCK_STATUS', { componentId, actionName });

                socket.once('LOCK_STATUS', (response: LockStatusResponse) => {
                    if (response.success) {
                        resolve(response.available);
                    } else {
                        resolve(false);
                    }
                });
            }
            setTimeout(() => {
                resolve(false);
            }, 2500);
        });
    }
}

export default LockService;
